import React from 'react';
import { Alert, Table } from 'react-bootstrap';

const formatCellValue = (value) => {
    if (value == null || value === '') return '—';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
};

const parseImportError = (err) => {
    if (typeof err !== 'string') {
        return { row: null, message: JSON.stringify(err) };
    }
    const match = err.match(/^Row\s+(\d+):\s*([\s\S]*)$/);
    if (match) {
        return { row: match[1], message: match[2].trim() };
    }
    return { row: null, message: err };
};

const CreatedItemsTable = ({ title, items }) => {
    if (!Array.isArray(items) || items.length === 0) return null;

    if (typeof items[0] === 'string') {
        return (
            <div className="mb-3">
                <strong className="small d-block mb-1">{title}</strong>
                <ul className="mb-0 small ps-3">
                    {items.map((item, index) => (
                        <li key={`${title}-${index}`} className="text-break">{item}</li>
                    ))}
                </ul>
            </div>
        );
    }

    const columns = Object.keys(items[0]).filter((key) => items.some((item) => item[key] != null && item[key] !== ''));

    return (
        <div className="mb-3">
            <strong className="small d-block mb-2">{title}</strong>
            <div className="table-responsive border rounded bg-white">
                <Table striped bordered hover size="sm" className="mb-0 align-middle">
                    <thead className="table-light">
                        <tr>
                            {columns.map((column) => (
                                <th key={column}>{column}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => (
                            <tr key={`${title}-row-${index}`}>
                                {columns.map((column) => (
                                    <td key={`${title}-${index}-${column}`} className="text-break">
                                        {formatCellValue(item[column])}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        </div>
    );
};

const ImportErrorsTable = ({ title, errors }) => {
    if (!Array.isArray(errors) || errors.length === 0) return null;

    const parsedErrors = errors.map(parseImportError);

    return (
        <div className="mb-3">
            <strong className="small d-block mb-2">{title}</strong>
            <div className="table-responsive border rounded bg-white">
                <Table striped bordered hover size="sm" className="mb-0 align-middle">
                    <thead className="table-light">
                        <tr>
                            <th style={{ width: '80px' }}>Ligne</th>
                            <th>Erreur</th>
                        </tr>
                    </thead>
                    <tbody>
                        {parsedErrors.map((item, index) => (
                            <tr key={`${title}-err-${index}`}>
                                <td className="text-center">{item.row ?? '—'}</td>
                                <td className="text-break" style={{ whiteSpace: 'pre-wrap' }}>
                                    {item.message}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        </div>
    );
};

const ReferencesSubcovImportResultAlert = ({ result, onDismiss }) => {
    if (!result || typeof result !== 'object') return null;

    const referencesCreated = Array.isArray(result.references_created) ? result.references_created : [];
    const subcoveragesCreated = Array.isArray(result.subcoverages_created) ? result.subcoverages_created : [];
    const referenceErrors = Array.isArray(result.reference_errors) ? result.reference_errors : [];
    const subcoverageErrors = Array.isArray(result.subcoverage_errors) ? result.subcoverage_errors : [];

    const createdCount = referencesCreated.length + subcoveragesCreated.length;
    const errorCount = referenceErrors.length + subcoverageErrors.length;
    const hasErrors = errorCount > 0;
    const variant = hasErrors ? (createdCount > 0 ? 'warning' : 'danger') : 'success';

    return (
        <Alert variant={variant} dismissible={!!onDismiss} onClose={onDismiss} className="mb-0">
            {result.message && (
                <p className="mb-2 fw-semibold">{result.message}</p>
            )}
            <p className="mb-3 small">
                <strong>Références créées :</strong> {referencesCreated.length}
                {' · '}
                <strong>Sous-couvertures créées :</strong> {subcoveragesCreated.length}
                {hasErrors && (
                    <>
                        {' · '}
                        <strong>Erreurs :</strong> {errorCount}
                    </>
                )}
            </p>

            <CreatedItemsTable title="Références créées" items={referencesCreated} />
            <CreatedItemsTable title="Sous-couvertures créées" items={subcoveragesCreated} />

            <ImportErrorsTable title="Erreurs références" errors={referenceErrors} />
            <ImportErrorsTable title="Erreurs sous-couvertures" errors={subcoverageErrors} />
        </Alert>
    );
};

export default ReferencesSubcovImportResultAlert;
